import { ReactNode } from 'react';
import {
  FolderOpen,
  FileText,
  Building2,
  PieChart,
  TrendingUp,
  Search,
  UserPlus,
  CheckCircle2,
} from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

/**
 * Generic empty state component
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
}: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center py-12 text-center', className)}>
      {icon && (
        <div className="rounded-full bg-muted p-4 mb-4">
          {icon}
        </div>
      )}
      <h3 className="font-semibold text-lg mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-muted-foreground max-w-sm mb-4">{description}</p>
      )}
      {(action || secondaryAction) && (
        <div className="flex gap-2">
          {action && (
            <Button onClick={action.onClick}>
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="outline" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Empty state for holdings/portfolio
 */
export function EmptyHoldings({
  onUpdateHoldings,
  className,
}: {
  onUpdateHoldings?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      icon={<PieChart className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
      title="No holdings yet"
      description="Connect your accounts or upload your holdings to see portfolio analysis and get personalized insights."
      action={onUpdateHoldings ? { label: 'Update Holdings', onClick: onUpdateHoldings } : undefined}
      className={className}
    />
  );
}

/**
 * Empty state for linked accounts
 */
export function EmptyLinkedAccounts({
  onLinkAccount,
  className,
}: {
  onLinkAccount?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      icon={<Building2 className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
      title="No accounts linked"
      description="Link your brokerage accounts to automatically import holdings and keep them in sync."
      action={onLinkAccount ? { label: 'Link Account', onClick: onLinkAccount } : undefined}
      className={className}
    />
  );
}

/**
 * Empty state for search results
 */
export function EmptySearchResults({
  query,
  onClear,
  className,
}: {
  query?: string;
  onClear?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      icon={<Search className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
      title="No results found"
      description={query ? `No matches found for "${query}". Try a different search term.` : 'No matches found for your search.'}
      action={onClear ? { label: 'Clear Search', onClick: onClear } : undefined}
      className={className}
    />
  );
}

/**
 * Empty state for partner/household view
 */
export function EmptyPartner({
  onInvitePartner,
  className,
}: {
  onInvitePartner?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      icon={<UserPlus className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
      title="No partner linked"
      description="Link your partner's portfolio to get a complete household view and better planning insights."
      action={onInvitePartner ? { label: 'Invite Partner', onClick: onInvitePartner } : undefined}
      className={className}
    />
  );
}

/**
 * Empty state for income sources
 */
export function EmptyIncomeSources({
  onAddSource,
  className,
}: {
  onAddSource?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      icon={<TrendingUp className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
      title="No income sources"
      description="Add your income sources like Social Security, pensions, or annuities to calculate your retirement income gap."
      action={onAddSource ? { label: 'Add Income Source', onClick: onAddSource } : undefined}
      className={className}
    />
  );
}

/**
 * Empty state for documents/notes
 */
export function EmptyDocuments({
  onAdd,
  className,
}: {
  onAdd?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      icon={<FileText className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
      title="No notes yet"
      description="Add notes to keep track of important information about your portfolio and financial planning."
      action={onAdd ? { label: 'Add Note', onClick: onAdd } : undefined}
      className={className}
    />
  );
}

/**
 * Empty state for insights
 */
export function EmptyInsights({
  className,
}: {
  className?: string;
}) {
  return (
    <div className={cn('bg-primary/5 border border-primary/20 rounded-lg p-6 text-center', className)}>
      <div className="flex justify-center mb-2">
        <CheckCircle2 className="h-6 w-6 text-primary" />
      </div>
      <h3 className="font-medium text-primary mb-1">Analysis Complete</h3>
      <p className="text-sm text-muted-foreground">
        No observations have been identified based on your current portfolio data.
      </p>
    </div>
  );
}

/**
 * Compact inline empty state for smaller sections
 */
export function InlineEmpty({
  message,
  className,
}: {
  message: string;
  className?: string;
}) {
  return (
    <div className={cn(
      'flex items-center justify-center py-8 text-sm text-muted-foreground',
      className
    )}>
      <FolderOpen className="h-4 w-4 mr-2" aria-hidden="true" />
      {message}
    </div>
  );
}
